<template>
  <WithBackdrop
    :class="[$style.config, $style.wrap]"
    :backdrop="data?.fields?.backdrop"
    @intersecting="onIntersecting"
  >
    <WithBackdrop
      v-if="computedActiveBackdrop"
      :key="activeTrack?.sys?.id + 'backdrop'"
      :class="$style.activeBackdrop"
      :backdrop="computedActiveBackdrop"
      :style="
        detailSize && {
          '--active-backdrop-bottom': `calc(100% - ${Math.round(
            detailSize.height,
          )}px - var(--container-gap) * 4)`,
        }
      "
    />
    <div :class="$style.container">
      <WithBoxSize :class="$style.detail" @size-change="onDetailSizeChanged">
        <header :class="$style.header">
          <BaseHeading as="h3">
            {{ data?.fields?.header }}
          </BaseHeading>
        </header>
        <PodcastsAndVideosPodcast
          v-if="instanceOfPodcast(activeTrack)"
          :key="activeTrack?.sys?.id"
          :data="activeTrack"
        />
        <PodcastsAndVideosVideoContent
          v-if="instanceOfVideoContent(activeTrack)"
          :key="activeTrack?.sys?.id"
          :data="activeTrack"
        />
      </WithBoxSize>

      <ul :class="$style.playlist">
        <li
          v-for="l in computedPlaylist"
          :key="l?.sys?.id"
          :class="[
            $style.playlistItem,
            activeTrack?.sys?.id === l?.sys?.id && $style.active,
          ]"
          @click="onRequestTrack(l)"
        >
          <PodcastsAndVideosListItem :data="l" />
        </li>
      </ul>
    </div>
  </WithBackdrop>
</template>

<script setup lang="ts">
import {
  instanceOfPodcast,
  instanceOfVideoContent,
} from '~/utils/instanceOfEntry'

import type {
  TypeContentBlockWithoutUnresolvableLinksResponse,
  TypePodcastWithoutUnresolvableLinksResponse,
  TypeVideoContentWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

const props = defineProps<{
  data: TypeContentBlockWithoutUnresolvableLinksResponse
}>()

const firstTrack = props.data?.fields?.references?.[0]
const defaultActiveTrack =
  instanceOfPodcast(firstTrack) || instanceOfVideoContent(firstTrack)
    ? firstTrack
    : null

const activeTrack = ref<
  | TypePodcastWithoutUnresolvableLinksResponse
  | TypeVideoContentWithoutUnresolvableLinksResponse
  | null
>(defaultActiveTrack)
const detailSize = ref({
  width: 0,
  height: 0,
})

const computedPlaylist = computed(() => {
  const list = props.data?.fields?.references || []
  return list.filter((l) => instanceOfPodcast(l) || instanceOfVideoContent(l))
})

const computedActiveBackdrop = computed(
  () => activeTrack.value?.fields?.poster?.fields?.file?.url,
)

const onIntersecting = () => {
  activeTrack.value = computedPlaylist.value?.[0]
}

const onRequestTrack = (
  track:
    | TypePodcastWithoutUnresolvableLinksResponse
    | TypeVideoContentWithoutUnresolvableLinksResponse,
) => {
  activeTrack.value = track
}

const onDetailSizeChanged = (state: {
  contentBoxSize: {
    height: number
    width: number
  }
  borderBoxSize: {
    height: number
    width: number
  }
}) => {
  detailSize.value = state.borderBoxSize
}
</script>

<style lang="scss" module>
.config {
  @include breakpoint-variables(
    '--wrap-padding',
    (
      base: 30px 0,
      sm: 40px 0,
      md: 60px 0,
    )
  );

  @include breakpoint-variables(
    '--active-backdrop-right',
    (
      base: 0,
      md: calc(50% - 10em),
      lg: calc(50% - 10em),
    )
  );

  --container-max-width: 1440px;

  @include breakpoint-variables(
    '--container-padding',
    (
      base: 0,
      sm: 0 1em,
      md: 0 2em,
    )
  );

  --header-color: var(--white);

  @include breakpoint-variables(
    '--container-grid-template-columns',
    (
      base: 1fr,
      sm: 1fr,
      md: 1fr 1fr,
      lg: 4fr 3fr,
    )
  );

  --container-gap: 0px;

  --playlist-color: var(--black);
  --playlist-max-width: 720px;

  @include breakpoint-variables(
    '--list-gap',
    (
      base: 0.5em,
      xs: 0.75em,
      sm: 1em,
    )
  );
}

.wrap {
  padding: var(--wrap-padding);
  overflow: hidden;
}

.activeBackdrop {
  display: var(--active-backdrop-display, block);
  backface-visibility: hidden; // remove flicker
  position: absolute !important;
  left: 0;
  top: 0;
  bottom: var(--active-backdrop-bottom);
  right: var(--active-backdrop-right);
  z-index: -1 !important;
  transform: translateZ(0); // safari is freaking out

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--black-alpha50);
  }
}

.container {
  max-width: var(--container-max-width);
  margin: 0 auto;
  padding: var(--container-padding);

  display: grid;
  grid-template-columns: var(--container-grid-template-columns);
  gap: var(--container-gap);
}

.detail {
  padding: 0 1.75rem;
  margin-bottom: 2em;
}

.header {
  color: var(--header-color);
  margin: 0 0 1.5em;
  padding: var(--header-padding);
}

.playlist {
  color: var(--playlist-color);
  @include list-unstyled();
  max-width: var(--playlist-max-width);
  margin: 0 auto;
  padding: 0 1.75rem;
  display: flex;
  gap: var(--list-gap);
  flex-direction: column;
}

.playlistItem {
  cursor: pointer;
  position: relative;

  &.active::before {
    pointer-events: none;
    content: '';
    position: absolute;
    inset: 0;
    border: 2px solid var(--theme-accent-color, var(--black));
    z-index: 1;
  }
}
</style>
